import { createEffect, createSignal, on, onCleanup } from "solid-js";

/**
 * Хук для плавной анимации изменения цены в SolidJS компонентах
 *
 * @example
 * // Простое использование
 * const [price, setPrice] = createSignal("100");
 * const animation = usePriceAnimation(() => price());
 *
 * // С кастомными настройками
 * const animation = usePriceAnimation(
 *   () => price(),
 *   { duration: 1000, easing: (t) => t * t }
 * );
 *
 * // В JSX
 * <div
 *   style={{ width: `${animation.animatedWidth()}px` }}
 * >
 *   {animation.animatedValue()}
 * </div>
 *
 * @example
 * // Работа с валютами
 * const priceRu = () => "1500";
 * const priceUsd = () => "20.50";
 * const isRussian = () => store.isRuCards;
 *
 * const currentPrice = () => isRussian() ? priceRu() : priceUsd();
 * const animation = usePriceAnimation(currentPrice);
 *
 * // Отображение с валютой
 * const displayPrice = () => {
 *   const value = animation.animatedValue();
 *   return isRussian() ? `${value} ₽` : `$${value}`;
 * };
 */

type UsePriceAnimationOptions = {
	duration?: number; // Продолжительность анимации в миллисекундах
	easing?: (t: number) => number; // Функция плавности
};

/**
 * Хук для плавной анимации изменения цены
 * @param targetPrice - целевая цена (строка или число)
 * @param options - опции анимации
 * @returns объект с анимированным значением цены и шириной
 */
export function usePriceAnimation(
	targetPrice: () => string | number | undefined | null,
	options: UsePriceAnimationOptions = {}
) {
	const { duration = 800, easing = easeOutCubic } = options;

	const [animatedValue, setAnimatedValue] = createSignal<number>(0);
	const [_animatedWidth, _setAnimatedWidth] = createSignal<number>(0);
	const [isAnimating, setIsAnimating] = createSignal(false);

	let animationId: number | null = null;
	let startValue = 0;
	const _startWidth = 0;
	let startTime = 0;

	function easeOutCubic(t: number): number {
		return 1 - (1 - t) ** 3;
	}

	function parsePrice(price: string | number | undefined | null): number {
		if (typeof price === "number") return price;
		if (!price) return 0;

		const cleanPrice = String(price).replace(/[^\d.,]/g, "");

		// Заменяем запятую на точку для корректного парсинга
		const normalizedPrice = cleanPrice.replace(",", ".");

		const parsed = Number.parseFloat(normalizedPrice);
		return Number.isNaN(parsed) ? 0 : parsed;
	}

	function animate(currentTime: number) {
		if (!startTime) startTime = currentTime;

		const elapsed = currentTime - startTime;
		const progress = Math.min(elapsed / duration, 1);

		const easedProgress = easing(progress);
		const currentValue = startValue + (parsePrice(targetPrice()) - startValue) * easedProgress;

		setAnimatedValue(currentValue);
		if (progress < 1) {
			animationId = requestAnimationFrame(animate);
		} else {
			setIsAnimating(false);
			animationId = null;
			startTime = 0;
		}
	}

	createEffect(
		on(targetPrice, (target) => {
			const newTarget = parsePrice(target);
			const currentAnimated = animatedValue();

			// Если значение изменилось, запускаем анимацию
			if (Math.abs(newTarget - currentAnimated) > 0.01) {
				// Останавливаем текущую анимацию если она идет
				if (animationId) {
					cancelAnimationFrame(animationId);
				}

				startValue = currentAnimated;
				startTime = 0;
				setIsAnimating(true);
				animationId = requestAnimationFrame(animate);
			}
		})
	);

	onCleanup(() => {
		if (animationId) {
			cancelAnimationFrame(animationId);
		}
	});

	const formattedValue = () => {
		const value = animatedValue();

		const target = targetPrice();
		const targetStr = String(target || "");
		const afterDot = targetStr.split(".").pop()?.length;

		// Если в целевой цене есть десятичные знаки, сохраняем их
		if (targetStr.includes(".") || targetStr.includes(",")) {
			return value.toFixed(afterDot);
		}

		return Math.round(value).toString();
	};

	return {
		animatedValue: formattedValue,
		isAnimating,
		rawValue: animatedValue
	};
}
